import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { CircleHelp } from 'lucide-react'
import {
    DataTableCustomized,
    type DataTableColumn,
} from '@/components/ui/data-table-customized'
import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@/components/ui/tooltip'
import { QuarterlyMetrics } from '@/modules/Survey/types/quarterly-report'

interface QuarterlyDataTableProps {
    data: QuarterlyMetrics[]
}

/** Header cell with a question-mark tooltip */
const HeaderWithTooltip = ({
    label,
    tooltip,
}: {
    label: string
    tooltip: string
}) => (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
        {label}
        <Tooltip>
            <TooltipTrigger asChild>
                <CircleHelp className="size-3.5 text-muted-foreground shrink-0" />
            </TooltipTrigger>
            <TooltipContent side="top">{tooltip}</TooltipContent>
        </Tooltip>
    </span>
)

export const QuarterlyDataTable = ({ data }: QuarterlyDataTableProps) => {
    const { t } = useTranslation('surveys')

    const columns = useMemo<DataTableColumn<QuarterlyMetrics>[]>(
        () => [
            {
                key: 'quarter',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.quarter')}
                        tooltip={t('surveys.quarterly_report.tooltip_quarter')}
                    />
                ),
                cellClassName: 'font-medium',
            },
            {
                key: 'active_customers_start',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.active_start')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_active_start'
                        )}
                    />
                ),
            },
            {
                key: 'churned_customers',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.churned')}
                        tooltip={t('surveys.quarterly_report.tooltip_churned')}
                    />
                ),
            },
            {
                key: 'new_customers',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.new')}
                        tooltip={t('surveys.quarterly_report.tooltip_new')}
                    />
                ),
            },
            {
                key: 'active_customers_end',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.active_end')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_active_end'
                        )}
                    />
                ),
            },
            {
                key: 'survey_participants',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.participants')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_participants'
                        )}
                    />
                ),
            },
            {
                key: 'rated_participants',
                title: t('surveys.quarterly_report.rated_participants'),
            },
            {
                key: 'high_satisfaction_rate',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.high_satisfaction')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_high_satisfaction'
                        )}
                    />
                ),
                render: (value) =>
                    value == null ? '—' : `${(value as number).toFixed(1)}%`,
                width: 170,
            },
            {
                key: 'medium_satisfaction_rate',
                title: (
                    <HeaderWithTooltip
                        label={t(
                            'surveys.quarterly_report.medium_satisfaction'
                        )}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_medium_satisfaction'
                        )}
                    />
                ),
                render: (value) =>
                    value == null ? '—' : `${(value as number).toFixed(1)}%`,
                width: 170,
            },
            {
                key: 'avg_service_rating',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.service_rating')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_service_rating'
                        )}
                    />
                ),
                render: (value) => (value as number | null)?.toFixed(1) ?? '—',
            },
            {
                key: 'avg_customer_care_rating',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.care_rating')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_care_rating'
                        )}
                    />
                ),
                render: (value) => (value as number | null)?.toFixed(1) ?? '—',
            },
            {
                key: 'avg_tech_support_rating',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.tech_rating')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_tech_rating'
                        )}
                    />
                ),
                render: (value) => (value as number | null)?.toFixed(1) ?? '—',
            },
            {
                key: 'avg_issue_resolution_rating',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.issue_rating')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_issue_rating'
                        )}
                    />
                ),
                render: (value) => (value as number | null)?.toFixed(1) ?? '—',
            },
            {
                key: 'avg_referral_willingness_rating',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.referral_rating')}
                        tooltip={t(
                            'surveys.quarterly_report.tooltip_referral_rating'
                        )}
                    />
                ),
                render: (value) => (value as number | null)?.toFixed(1) ?? '—',
            },
            {
                key: 'upsale_potential',
                title: (
                    <HeaderWithTooltip
                        label={t('surveys.quarterly_report.upsale')}
                        tooltip={t('surveys.quarterly_report.tooltip_upsale')}
                    />
                ),
                render: (_, row) => {
                    const up = row.upsale_potential
                    if (!up) return '—'
                    return `${up.cloud ?? 0}/${up.cdn ?? 0}/${up.vnis ?? 0}`
                },
            },
        ],
        [t]
    )

    if (!data || data.length === 0) {
        return (
            <div className="text-center py-8 text-gray-500">
                {t('surveys.quarterly_report.no_data')}
            </div>
        )
    }

    return (
        <DataTableCustomized<QuarterlyMetrics> columns={columns} rows={data} />
    )
}
